import React, { useMemo } from 'react';
import ScoreCard from './scoreCard';
import PromoCard from './PromoCard';
import Court from './court';

interface MatchFeedWithPromoProps {
  data: any[];
  transformedLiveMatchdata: any;
  handleShareClick: (matchuuid: string) => (event: React.MouseEvent<HTMLButtonElement>) => void;
  isAlertOpen: boolean;
  onLearnMore: () => void;
  onStartForFree: () => void;
  breakpoint: string;
}

const MatchFeedWithPromo: React.FC<MatchFeedWithPromoProps> = ({
  data,
  transformedLiveMatchdata,
  handleShareClick,
  isAlertOpen,
  onLearnMore,
  onStartForFree,
  breakpoint,
}) => {
  // Check if we should show mobile promo cards
  const shouldShowMobilePromo = useMemo(() => {
    // Show mobile promo on xs and sm breakpoints
    return breakpoint === 'xs' || breakpoint === 'sm';
  }, [breakpoint]);

  // Calculate total match count to determine promo card placement
  const totalMatchCount = useMemo(() => {
    let count = 0;
    data?.forEach((item: any) => {
      item?.info?.forEach((info: any) => {
        if (info?.cmatches?.length > 0) {
          count += info.cmatches.length;
        }
      });
    });
    return count;
  }, [data]);

  // Determine promo card positions based on match count
  const promoPositions = useMemo(() => {
    const positions: number[] = [];

    if (!shouldShowMobilePromo) {
      return positions;
    }

    // For small tournaments (< 6 matches), show one promo near the end
    if (totalMatchCount < 6) {
      if (totalMatchCount >= 3) {
        positions.push(totalMatchCount - 1);
      }
      return positions;
    }

    // First promo after 4-5 matches
    positions.push(4);

    // Second promo after another 8-10 matches (total 12-15 matches in)
    if (totalMatchCount >= 13) {
      positions.push(13);
    }

    return positions;
  }, [shouldShowMobilePromo, totalMatchCount]);

  // Render the feed with promo cards inserted at the right positions
  const renderFeedWithPromo = () => {
    let globalMatchIndex = 0;
    const elements: JSX.Element[] = [];
    let promoCardCount = 0;

    data?.forEach((dateItem: any, dateIndex: number) => {
      dateItem?.info?.forEach((courtInfo: any, courtIndex: number) => {
        const hasMatches = courtInfo?.cmatches?.length > 0;

        if (!hasMatches) return;

        const courtKey = `court-${dateIndex}-${courtIndex}`;

        // Render court header
        elements.push(
          <div className="flex flex-col" key={courtKey}>
            {courtInfo.court ? (
              <Court court={courtInfo.court} type="court" />
            ) : (
              <Court
                court={courtInfo.teams}
                type="teams"
                tieDescription={courtInfo.tieDescription}
              />
            )}

            {/* Render matches for this court */}
            {courtInfo?.cmatches?.map((cmatches: any, matchIndex: number) => {
              const currentMatchIndex = globalMatchIndex;
              globalMatchIndex++;

              // Check if we should insert a promo card before this match
              const shouldInsertPromo =
                shouldShowMobilePromo &&
                promoPositions.includes(currentMatchIndex) &&
                promoCardCount < 2;

              // Update match score if it matches live data
              if (cmatches.matchId === transformedLiveMatchdata.matchId) {
                cmatches.score = transformedLiveMatchdata.score;
                cmatches.status = transformedLiveMatchdata.status;
              }

              // Prefix match description with court name for team events
              const courtName = cmatches.court;
              const prefixedDescription = courtName && cmatches.matchDescription
                ? `${courtName} - ${cmatches.matchDescription} `
                : cmatches.matchDescription
                  ? cmatches.matchDescription + " "
                  : undefined;

              const matchKey = `match-${dateIndex}-${courtIndex}-${matchIndex}`;

              return (
                <React.Fragment key={matchKey}>
                  {shouldInsertPromo && (() => {
                    promoCardCount++;
                    return (
                      <PromoCard
                        key={`promo-${promoCardCount}`}
                        onLearnMore={onLearnMore}
                        onStartForFree={onStartForFree}
                      />
                    );
                  })()}

                  <ScoreCard
                    matchId={cmatches.matchId}
                    matchDescription={prefixedDescription}
                    matchTypeName={
                      cmatches.matchTypeName !== null
                        ? cmatches.matchTypeName
                        : null
                    }
                    status={cmatches.status ? cmatches.status : undefined}
                    players={cmatches.players ? cmatches.players : undefined}
                    score={cmatches.score ? cmatches.score : undefined}
                    handleShare={handleShareClick(
                      cmatches.matchuuid ?? cmatches.matchId
                    )}
                    isAlertOpen={isAlertOpen}
                  />
                </React.Fragment>
              );
            })}
          </div>
        );
      });
    });

    return elements;
  };

  return <>{renderFeedWithPromo()}</>;
};

export default MatchFeedWithPromo;
